import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import paths from 'app/paths'
import { useSmartEntity, useExportSmartEntity } from 'contexts/smart-entity'
import { useAccess } from 'contexts/access'
import { useUpgradable } from 'hooks/upgradable'
import { getSmartTemplateUrl } from 'utilities/url'
import { refreshSmartTemplates } from 'pages/documents/utilities'
import { ContextMenu } from '../../s'
import { Scrollable as Modal } from 'modals/generic'
import AddToTemplates from 'pages/documents/modals/add-to-templates'
import {
    Edit,
    FilePlus as UseTemplate,
    FolderPlus as AddToOurTemplates,
    DownloadCloud as ExportTemplate,
    Link as CopyLink,
    Trash2 as Delete
} from 'styled-icons/feather'
import { SettingsDocument } from 'components/feather'

const PreviewSmartTemplateDetailsActions = ({ listTemplate = null, setEditing, viewContext = 'modal', slim = false, dismiss, salt }) => {
    const { formatMessage } = useIntl()

    const {
        entity,
        remove: removeTemplate
    } = useSmartEntity()

    const {
        check,
        checkFeature
    } = useAccess()

    const checkUpgradable = useUpgradable()

    const smartTemplatesAccess = check('smart-document-templates:manage')
    const smartTemplatesImportExportAvailable = checkFeature('smart-document-templates-import-export')
    const smartTemplatesImportExportUpgradable = checkUpgradable({ feature: 'smart-document-templates-import-export' })

    const [destructing, setDestructing] = useState(false)
    const [addingToTemplates, setAddingToTemplates] = useState(null)

    const exportTemplate = useExportSmartEntity()

    const template = entity?.id ?
        entity :
        (listTemplate ?? {})

    const { providedBy } = template ?? {}

    const actions = {
        editTemplate: () => {
            if(!smartTemplatesAccess || providedBy || viewContext === 'page' || slim) {
                return null
            }

            return {
                salt: `${salt}:editor`,
                icon: <Edit size={24} />,
                label: formatMessage({
                    id: 'template_action_edit',
                    defaultMessage: 'Edit template'
                }),
                element: 'link',
                link: { to: getSmartTemplateUrl(template) },
                effect: 'neutral'
            }
        },
        settings: () => {
            if(!smartTemplatesAccess || providedBy || slim) {
                return null
            }

            return {
                salt: `${salt}:edit:details`,
                icon: <SettingsDocument size={24} />,
                label: formatMessage({
                    id: 'template_action_settings',
                    defaultMessage: 'Template settings'
                }),
                effect: 'neutral',
                disabled: !!listTemplate,
                onClick: () => setEditing(true)
            }
        },
        addToTemplates: () => {
            if(!providedBy) {
                return null
            }

            return {
                salt: `${salt}:add-to-templates`,
                icon: <AddToOurTemplates size={24} />,
                label: formatMessage({
                    id: 'template_action_add_to_templates',
                    defaultMessage: 'Add to our templates'
                }),
                effect: 'neutral',
                disabled: !!listTemplate,
                onClick: () => setAddingToTemplates(template)
            }
        },
        useTemplate: () => ({
            salt: `${salt}:use`,
            icon: <UseTemplate size={24} />,
            label: formatMessage({
                id: 'template_action_use',
                defaultMessage: 'Use template'
            }),
            element: 'link',
            link: {
                to: paths.documents.base,
                state: {
                    intent: 'add',
                    template
                }
            },
            disabled: !!listTemplate,
            effect: 'neutral'
        }),
        exportTemplate: () => {
            if(!smartTemplatesAccess || providedBy) {
                return null
            }

            if(!smartTemplatesImportExportAvailable && !smartTemplatesImportExportUpgradable) {
                return null
            }

            return {
                salt: `${salt}:export`,
                icon: <ExportTemplate size={24} />,
                label: formatMessage({
                    id: 'action_export',
                    defaultMessage: 'Export'
                }),
                effect: 'neutral',
                onClick: () => exportTemplate(template),
                ...(smartTemplatesImportExportUpgradable ? {
                    upgrade: {
                        feature: 'smart-document-templates-import-export'
                    }
                } : null)
            }
        },
        copyLink: () => {
            if(template.providedBy) {
                return null
            }

            return {
                salt: `${salt}:copy-link`,
                icon: <CopyLink size={24} />,
                label: formatMessage({
                    id: 'action_copy_link',
                    defaultMessage: 'Copy link'
                }),
                effect: 'neutral',
                onClick: () => global.navigator.clipboard.writeText(`${window.location.origin}${paths.documents.templates}?id=${template.id}`)
            }
        },
        remove: () => {
            if(!smartTemplatesAccess || providedBy || slim) {
                return null
            }

            return {
                salt: `${salt}:delete`,
                icon: <Delete size={24} />,
                label: formatMessage({
                    id: 'action_delete',
                    defaultMessage: 'Delete'
                }),
                effect: 'destructive',
                className: destructing ? 'loading' : null,
                onClick: async () => {
                    setDestructing(true)
                    const { ok } = await removeTemplate()

                    if(ok) {
                        if(viewContext === 'modal') {
                            refreshSmartTemplates()
                        }
                        dismiss?.()
                    } else {
                        setDestructing(false)
                    }
                },
                prompt: {
                    question: formatMessage({
                        id: 'template_action_delete_prompt_question',
                        defaultMessage: 'Confirm deleting {template}'
                    }, { template: template.name }),
                    confirmText: formatMessage({
                        id: 'action_delete',
                        defaultMessage: 'Delete'
                    })
                }
            }
        }
    }

    return (
        <>
            <ContextMenu
                actions={actions}
                context={template}
                salt={salt} />
            <Modal
                show={!!addingToTemplates}
                dismiss={() => setAddingToTemplates(false)}>
                <AddToTemplates
                    mode="predefined"
                    template={addingToTemplates}
                    salt={salt} />
            </Modal>
        </>
    )
}


export default PreviewSmartTemplateDetailsActions